// Chat footer for module-owned datasets.
//
// Renders one section per enabled module for the caller's org, with a
// multi-select checkbox per dataset. Selections are sticky per-user:
// loaded once at mount, persisted on every checkbox toggle.
//
// Hidden entirely when the org has no enabled modules — that's the PRD's
// "module disabled → footer disappears on next load" behavior.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from '../shared/Icon';
import Chip from '../shared/Chip';
import { apiService } from '../../../services/api';
import './ModuleLibraryFooter.css';

// Classify a module dataset for the library list. Mirrors the personal
// "Datasets" card (AvailableDatasets): a dataset is failed when its batch job
// errored and it isn't otherwise queryable, and ready (selectable) only once
// ≥1 file has finished processing (server `queryable` flag, PRD-958).
function classifyDataset(d) {
  const job = (d.job_status || '').toLowerCase();
  const isReady = d.queryable === true;
  const isFailed = (job === 'failed' || job === 'error') && !isReady;
  return { isReady, isFailed };
}

function ModuleLibraryFooter({ onShowNotification, onSelectionChange }) {
  const [modules, setModules] = useState([]);
  // Map of moduleId -> array of dataset rows (each: { id, name, ... module_id, ... }).
  const [datasetsByModule, setDatasetsByModule] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsed, setCollapsed] = useState(true);

  // Initial load: enabled modules + sticky selections (in parallel),
  // then per-module dataset lists.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [moduleList, selectionResp] = await Promise.all([
          apiService.getMyModules(),
          apiService.getMyModuleSelections(),
        ]);
        if (cancelled) return;
        const enabledModules = moduleList?.modules || [];
        setModules(enabledModules);
        setSelectedIds(new Set(selectionResp?.dataset_ids || []));

        // Fetch datasets per module — small fan-out, mirrors how the chat
        // already calls getDatasets for the right sidebar.
        const entries = await Promise.all(
          enabledModules.map((m) =>
            apiService
              .getDatasets({ module_id: m.module_id, status: 'published', page_size: 100 })
              .then((r) => [m.module_id, r?.items || []])
              .catch(() => [m.module_id, []])
          )
        );
        if (cancelled) return;
        setDatasetsByModule(Object.fromEntries(entries));
      } catch (err) {
        if (!cancelled) onShowNotification?.(err.message || 'Failed to load SOP Library', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [onShowNotification]);

  // Bubble selection changes up so AskResAI can include them in the next query.
  useEffect(() => {
    onSelectionChange?.(Array.from(selectedIds));
  }, [selectedIds, onSelectionChange]);

  // Failed SOPs are hidden from the library entirely — they can't be used as
  // context, so they shouldn't appear or be selectable.
  const allRows = useMemo(
    () =>
      modules
        .flatMap((m) => (datasetsByModule[m.module_id] || []).map((d) => ({ ...d, _module: m })))
        .filter((d) => !classifyDataset(d).isFailed),
    [modules, datasetsByModule],
  );

  const filteredByModule = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const result = {};
    for (const m of modules) {
      const rows = (datasetsByModule[m.module_id] || []).filter((d) => !classifyDataset(d).isFailed);
      result[m.module_id] = q
        ? rows.filter((d) => (d.name || '').toLowerCase().includes(q))
        : rows;
    }
    return result;
  }, [modules, datasetsByModule, searchTerm]);

  const toggle = useCallback(async (datasetId) => {
    // Guard: only ready (queryable) SOPs can be selected. Processing ones stay
    // visible but inert until a file finishes.
    const row = allRows.find((d) => d.id === datasetId);
    if (row && !classifyDataset(row).isReady) return;
    // Optimistic flip; roll back on PUT error.
    const next = new Set(selectedIds);
    if (next.has(datasetId)) next.delete(datasetId);
    else next.add(datasetId);
    setSelectedIds(next);
    try {
      const resp = await apiService.setMyModuleSelections(Array.from(next));
      // Server is authoritative — it silently drops invalid ids. Trust its
      // canonical list rather than the optimistic set.
      setSelectedIds(new Set(resp?.dataset_ids || []));
    } catch (err) {
      // Roll back to the prior set.
      setSelectedIds(selectedIds);
      onShowNotification?.(err.message || 'Failed to save selection', 'error');
    }
  }, [selectedIds, onShowNotification, allRows]);

  // Hide the entire footer when there are no enabled modules — matches the
  // PRD's "module disabled → footer disappears on next load" behavior.
  if (!loading && modules.length === 0) return null;

  const selectedCount = selectedIds.size;
  const totalCount = allRows.length;

  return (
    <section className="module-library-footer">
      <header className="module-library-footer-header">
        <div className="module-library-footer-title">
          <Icon name="menu_book" className="module-library-footer-icon" size={18} />
          <span>SOP LIBRARY — ORG REFERENCE</span>
        </div>
        <div className="module-library-footer-summary">
          {selectedCount > 0 && (
            <Chip variant="success" className="module-library-footer-pill">
              {selectedCount} applied
            </Chip>
          )}
          <span className="module-library-footer-count">{totalCount} available</span>
          <button
            type="button"
            className="module-library-footer-toggle"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand SOP Library' : 'Minimize SOP Library'}
            title={collapsed ? 'Expand' : 'Minimize'}
          >
            <Icon name={collapsed ? 'expand_more' : 'expand_less'} />
          </button>
        </div>
      </header>

      {!collapsed && (
        <div className="module-library-footer-body">
        <div className="module-library-footer-search">
          <input
            type="search"
            className="module-library-footer-search-input"
            placeholder={`Search ${totalCount} SOPs in the org library…`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

      {loading && <p className="module-library-footer-status">Loading…</p>}

      {!loading && modules.map((m) => {
        const rows = filteredByModule[m.module_id] || [];
        if (rows.length === 0) return null;
        return (
          <div key={m.module_id} className="module-library-footer-section">
            {modules.length > 1 && (
              <div className="module-library-footer-section-title">{m.name}</div>
            )}
            <ul className="module-library-footer-list">
              {rows.map((d) => {
                const checked = selectedIds.has(d.id);
                const { isReady } = classifyDataset(d);
                const rowClass = [
                  'module-library-footer-row',
                  checked ? 'module-library-footer-row--checked' : '',
                  !isReady ? 'module-library-footer-row--processing' : '',
                ].filter(Boolean).join(' ');
                return (
                  <li key={d.id} className={rowClass}>
                    <label className="resui-checkbox module-library-footer-row-label">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!isReady}
                        onChange={() => toggle(d.id)}
                        className="module-library-footer-checkbox resui-checkbox-input"
                      />
                      <span className="resui-checkbox-mark" />
                      <span className="module-library-footer-row-text">
                        <span className="module-library-footer-row-name">{d.name}</span>
                        {d.description && (
                          <span className="module-library-footer-row-meta">{d.description}</span>
                        )}
                      </span>
                      {!isReady && (
                        <span
                          className="module-library-footer-row-spinner"
                          title="Still processing — not yet selectable"
                          aria-label="Processing"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                          </svg>
                        </span>
                      )}
                    </label>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}

      {!loading && totalCount === 0 && (
        <p className="module-library-footer-status">No SOPs in the library yet.</p>
      )}
        </div>
      )}
    </section>
  );
}

export default ModuleLibraryFooter;
